import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Spinner } from "reactstrap";
import {
  formatWaitDuration,
  listWaitingRoomDisplay,
  WaitingRoomEntry,
} from "../../../../services/waitingRoomService";

interface WaitingRoomTvOverlayProps {
  open: boolean;
  unitId: number | null;
  date: string;
  onClose: () => void;
}

const WaitingRoomTvOverlay = ({
  open,
  unitId,
  date,
  onClose,
}: WaitingRoomTvOverlayProps): React.JSX.Element | null => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState<WaitingRoomEntry[]>([]);

  const load = useCallback(async () => {
    if (!unitId || !open) {
      return;
    }

    setLoading(true);
    try {
      const response = await listWaitingRoomDisplay({
        unit_id: unitId,
        date,
      });
      setEntries(Array.isArray(response.data) ? response.data : []);
    } catch {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [date, open, unitId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      void load();
    }, 10000);

    return () => window.clearInterval(timer);
  }, [load, open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, open]);

  if (!open) {
    return null;
  }

  return (
    <div
      className="waiting-room-tv-overlay position-fixed top-0 start-0 w-100 h-100 d-flex flex-column"
      style={{
        zIndex: 1080,
        background:
          "radial-gradient(circle at top, #1f2a37 0%, #0b1220 55%, #05080f 100%)",
        color: "#f8fafc",
      }}
    >
      <div className="d-flex align-items-center justify-content-between px-4 py-3">
        <div>
          <div className="text-uppercase small opacity-75 letter-spacing-1">
            {t("studio.agenda.waitingRoom.tv.eyebrow")}
          </div>
          <h2 className="mb-0 fw-semibold">
            {t("studio.agenda.waitingRoom.tv.title")}
          </h2>
        </div>
        <Button color="light" outline onClick={onClose}>
          {t("studio.agenda.waitingRoom.tv.close")}
        </Button>
      </div>

      <div className="flex-grow-1 overflow-auto px-4 pb-4">
        {loading && entries.length === 0 ? (
          <div className="h-100 d-flex align-items-center justify-content-center">
            <Spinner color="light" />
          </div>
        ) : entries.length === 0 ? (
          <div className="h-100 d-flex flex-column align-items-center justify-content-center text-center opacity-75">
            <div className="display-6 mb-2">
              {t("studio.agenda.waitingRoom.empty.title")}
            </div>
            <div>{t("studio.agenda.waitingRoom.tv.emptyHint")}</div>
          </div>
        ) : (
          <div className="row g-3">
            {entries.map((entry) => {
              const isCalled = entry.waiting_room_status === "called";
              return (
                <div className="col-12 col-md-6 col-xl-4" key={entry.id}>
                  <div
                    className="rounded-4 p-4 h-100"
                    style={{
                      background: isCalled
                        ? "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
                        : "rgba(255,255,255,0.08)",
                      border: "1px solid rgba(255,255,255,0.12)",
                      minHeight: 160,
                    }}
                  >
                    <div className="small text-uppercase opacity-75 mb-2">
                      {t(
                        `studio.agenda.waitingRoom.status.${entry.waiting_room_status || "waiting"}`
                      )}
                    </div>
                    <div
                      className="fw-semibold mb-2"
                      style={{ fontSize: "2.4rem", lineHeight: 1.1 }}
                    >
                      {entry.client?.first_name || "—"}
                    </div>
                    <div className="d-flex justify-content-between align-items-end gap-2">
                      <div className="opacity-75">
                        {entry.professional?.first_name
                          ? t("studio.agenda.waitingRoom.tv.withProfessional", {
                              name: entry.professional.first_name,
                            })
                          : null}
                      </div>
                      <div className="fs-4 fw-medium">
                        {formatWaitDuration(entry.wait_seconds)}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default WaitingRoomTvOverlay;
